<script lang="ts">
  import { Accordion, AccordionItem, getModalStore, getToastStore } from '@skeletonlabs/skeleton'
  import { onMount, type SvelteComponent } from 'svelte'

  import { type ICreateModel } from '../models/models-store.svelte'
  import {
    AugmentationParams,
    type ClassWeighting,
    classWeightingValues,
    DatasetSplit,
    DEFAULT_IMAGE_SIZE,
    DEFAULT_LEARNING_RATE_FINDER_MAX_LR_STEP,
    DEFAULT_LEARNING_RATE_FINDER_MIN_LR_STEP,
    DEFAULT_LEARNING_RATE_FINDER_NUMBER_OF_TRAINING_STEPS,
    DEFAULT_TRAINER_BATCH_SIZE,
    DEFAULT_TRAINER_MAX_EPOCHS,
    DEFAULT_TRAINER_PATIENCE,
    displayClassWeighting,
    displayDatasetSplit,
    displayScenarioTypes,
    type HyperParametersWithRequiredField,
    type HyperParamFields,
    LrFinderParams,
    RunInfo,
    ScenarioType,
    scenarioTypesValues,
    type TrainerFields,
    type TrainerWithRequiredField,
    UnlearningHyperparameters,
  } from '../runs/unlearning-run.svelte'
  import CloseModalButton from '@components/CloseModalButton.svelte'
  import KwargsInput from '@components/KwargsInput.svelte'
  import MetadataFields from '@components/MetadataFields.svelte'
  import Select from '@components/Select.svelte'
  import {
    getStoragePrefix,
    storageConfigStore,
  } from '@routes/storage-configs/storage-configs-store'
  import { localStoragePrefixes } from '@stores/local-storage.svelte'
  import { loggedInStore } from '@stores/logged-in-store'
  import { HirundoCSV } from '@utils/hirundo-csv.svelte'
  import { KwargError, KwargType } from '@utils/kwargs.svelte'
  import { setRestoreState } from '@utils/modal-restore'
  import { ValidityState } from '@utils/validation.svelte'

  import Icon from '$lib/components/Icon.svelte'

  interface Props {
    /** Exposes parent props to this component. */
    parent: SvelteComponent
  }

  type AccordionEvent = CustomEvent<{
    event?: Event
    id: string
    panelId: string
    open: boolean
    autocollapse: boolean
  }>

  let { parent }: Props = $props()

  let useAdvancedRunOptions = $state(false)

  const primaryOrganizationId = $loggedInStore.primaryOrganizationId
  let runInfo = $state(new RunInfo(primaryOrganizationId))
  let invalids = $state({
    taskCsv: new ValidityState(),
  })

  let unlearningForm: HTMLFormElement | null = $state(null)

  let augmentationKwargsInput: KwargsInput | null = $state(null)
  let optimizerKwargsInput: KwargsInput | null = $state(null)
  let lrSchedulerKwargsInput: KwargsInput | null = $state(null)
  let lossFunctionKwargsInput: KwargsInput | null = $state(null)

  const modalStore = getModalStore()
  const restoreKey: string | undefined = $modalStore[0]?.meta?.restoreKey

  const toastStore = getToastStore()
  let activeToast: string | null = null

  type HyperJSON = ReturnType<typeof UnlearningHyperparameters.prototype.toJSON>
  interface RunUnlearningRestoreState {
    useAdvancedRunOptions: boolean
    runInfo: {
      organizationId: number
      name: string
      scenario: ScenarioType
      hyperparameters: HyperJSON | null
      unlearningTaskInfo: { csvUrl: string } | null
      predictAtEnd: DatasetSplit[]
    }
  }
  const isRunUnlearningRestoreState = (v: unknown): v is RunUnlearningRestoreState =>
    !!v && typeof v === 'object' && 'runInfo' in (v as Record<string, unknown>)

  // Restore previously cached state if present
  onMount(() => {
    if (
      $modalStore[0]?.meta?.restoreState &&
      isRunUnlearningRestoreState($modalStore[0].meta.restoreState)
    ) {
      const restoreState = $modalStore[0].meta.restoreState
      useAdvancedRunOptions = restoreState.useAdvancedRunOptions
      const newRunInfo = new RunInfo(restoreState.runInfo.organizationId)
      newRunInfo.name = restoreState.runInfo.name
      newRunInfo.scenario = restoreState.runInfo.scenario
      newRunInfo.predictAtEnd = restoreState.runInfo.predictAtEnd
      newRunInfo.unlearningTaskInfo = restoreState.runInfo.unlearningTaskInfo
        ? new HirundoCSV(restoreState.runInfo.unlearningTaskInfo.csvUrl)
        : null
      newRunInfo.hyperparameters = restoreState.runInfo.hyperparameters
        ? UnlearningHyperparameters.fromJSON(restoreState.runInfo.hyperparameters)
        : null
      runInfo = newRunInfo
    }
  })

  $effect(() => {
    if (!restoreKey) {
      return
    }
    setRestoreState(restoreKey, {
      useAdvancedRunOptions,
      runInfo: {
        organizationId: runInfo.organizationId,
        name: runInfo.name,
        scenario: runInfo.scenario,
        hyperparameters: runInfo.hyperparameters?.toJSON?.() ?? null,
        unlearningTaskInfo: runInfo.unlearningTaskInfo
          ? { csvUrl: runInfo.unlearningTaskInfo.csvUrl }
          : null,
        predictAtEnd: runInfo.predictAtEnd,
      },
    } satisfies RunUnlearningRestoreState)
  })

  const onFormCancel = () => {
    if ($modalStore?.[0]?.response) {
      $modalStore[0].response(false)
    }
    modalStore.close()
  }
  const onFormSubmit = () => {
    if (activeToast) {
      toastStore.close(activeToast)
    }

    if (!unlearningForm?.reportValidity()) {
      return
    }
    try {
      augmentationKwargsInput?.validateKwargs()
      optimizerKwargsInput?.validateKwargs()
      lrSchedulerKwargsInput?.validateKwargs()
      lossFunctionKwargsInput?.validateKwargs()
    } catch (error) {
      if (error instanceof KwargError) {
        toastStore.trigger({
          message: error.message,
          background: 'variant-filled-error',
        })
        return
      } else {
        console.error(error)
        throw error
      }
    }

    if ($modalStore[0].response) {
      $modalStore[0].response($state.snapshot(runInfo))
    }
    modalStore.close()
  }

  const accordionChange = (event: AccordionEvent) => {
    useAdvancedRunOptions = event.detail.open
    if (useAdvancedRunOptions && !runInfo.hyperparameters) {
      runInfo.hyperparameters = new UnlearningHyperparameters()
    }
  }

  const prepareHyperParameters = <T extends HyperParamFields>(
    runInfo: RunInfo,
    field: T,
  ): runInfo is RunInfo & {
    hyperparameters: HyperParametersWithRequiredField<T>
  } => {
    if (!runInfo.hyperparameters) {
      runInfo.hyperparameters = new UnlearningHyperparameters()
    }
    runInfo.hyperparameters.prepare(field)

    return true
  }

  const prepareTrainerHyperParameters = <T extends TrainerFields>(
    runInfo: RunInfo,
    field: T,
  ): runInfo is RunInfo & {
    hyperparameters: {
      trainer: TrainerWithRequiredField<T>
    }
  } => {
    if (prepareHyperParameters(runInfo, 'trainer')) {
      runInfo.hyperparameters.trainer.prepare(field)
      return true
    }
    return false
  }

  const model = $derived($modalStore[0].value as ICreateModel)
  const modelStorageConfig = $derived.by(() => {
    const config = $storageConfigStore.get(model.storageConfigId)
    if (!config) {
      throw new Error(`Storage config with ID ${model.storageConfigId} not found`)
    }
    return config
  })

  const dataPrefix = $derived.by(() =>
    getStoragePrefix({
      storageInputValues: {},
      selectedStorageConfig: modelStorageConfig,
      type: modelStorageConfig.type,
      localStoragePrefix: localStoragePrefixes.datasetUrlPrefix,
    }),
  )

  const togglePredictAtEnd = (splitValue: DatasetSplit) => {
    if (!runInfo.predictAtEnd) {
      runInfo.predictAtEnd = []
    }
    if (runInfo.predictAtEnd.includes(splitValue)) {
      runInfo.predictAtEnd = runInfo.predictAtEnd.filter((split) => split !== splitValue)
    } else {
      runInfo.predictAtEnd.push(splitValue)
    }
  }

  $effect(() => {
    console.log('runInfo changed', $state.snapshot(runInfo))
  })
</script>

{#if $modalStore[0]}
  <div class="card w-modal max-h-[90vh] space-y-4 overflow-y-scroll px-4 shadow-xl" role="dialog">
    <h3 class="h3 sticky top-0 z-10 justify-between bg-surface-50 p-4 dark:bg-surface-800">
      {$modalStore[0].title}<CloseModalButton onClose={parent.onClose} />
    </h3>
    <article class="space-y-4 p-4">
      {$modalStore[0].body}
      <div class="mt-4">
        <form bind:this={unlearningForm}>
          <div>
            <label class="label pb-2" for="taskCsv">Task CSV:</label>
            <MetadataFields
              prefix={dataPrefix}
              {invalids}
              fieldName="taskCsv"
              fieldValue={runInfo.unlearningTaskInfo?.csvUrl ?? ''}
              setField={(value: string) => {
                if (!runInfo.unlearningTaskInfo) {
                  runInfo.unlearningTaskInfo = new HirundoCSV()
                }
                runInfo.unlearningTaskInfo.csvUrl = value
                if (value.replace(dataPrefix, '')) {
                  invalids.taskCsv.setValid()
                } else {
                  invalids.taskCsv.setInvalid('Task CSV is required')
                }
              }}
            />
          </div>
        </form>
      </div>
      <div>
        <div>
          <p class="label">Run prediction on the following subsets at the end:</p>
          <div class="mt-2 flex flex-wrap gap-2">
            {#each Object.values(DatasetSplit) as splitValue (splitValue)}
              <button
                class="chip {runInfo.predictAtEnd?.includes(splitValue)
                  ? 'variant-filled'
                  : 'variant-ghost'}"
                onclick={() => {
                  togglePredictAtEnd(splitValue)
                }}
              >
                {#if runInfo.predictAtEnd?.includes(splitValue)}<span
                    ><Icon icon="iconoir:check" /></span
                  >
                {:else}
                  <span><Icon icon="iconoir:xmark" /></span>
                {/if}
                <span class="capitalize">{displayDatasetSplit[splitValue]}</span>
              </button>
            {/each}
          </div>
        </div>
      </div>
      <div>
        <div>
          <label class="label" for="scenario">Scenario:</label>
          <Select
            id="scenario"
            name="scenario"
            value={runInfo.scenario}
            onchange={(e) => {
              runInfo.scenario = e.currentTarget.value as ScenarioType
            }}
          >
            {#each scenarioTypesValues as scenarioType (scenarioType)}
              <option value={scenarioType}>{displayScenarioTypes[scenarioType]}</option>
            {/each}
          </Select>
        </div>
      </div>
      <Accordion class="card variant-glass-surface mt-4">
        <AccordionItem on:toggle={accordionChange}>
          {#snippet lead()}
            <Icon icon="iconoir:settings" class="h-6 w-6" />
          {/snippet}
          {#snippet summary()}
            <h3>Advanced run options</h3>
          {/snippet}
          {#snippet content()}
            {#if useAdvancedRunOptions && runInfo.hyperparameters}
              <form class="space-y-4">
                <div class="grid gap-2">
                  <h5 class="h5">Augmentations</h5>
                  <label
                    class="label flex cursor-pointer items-center space-x-2"
                    for="customAugmentations"
                  >
                    <input
                      id="customAugmentations"
                      name="customAugmentations"
                      type="checkbox"
                      class="checkbox"
                      checked={runInfo.hyperparameters.augmentations !== null}
                      onchange={(e) => {
                        if (e.currentTarget.checked) {
                          if (prepareHyperParameters(runInfo, 'augmentations')) {
                            runInfo.hyperparameters.augmentations = [new AugmentationParams()]
                          }
                        } else if (runInfo.hyperparameters) {
                          runInfo.hyperparameters.augmentations = null
                        }
                      }}
                    /><span>Use custom augmentations</span>
                  </label>
                  {#if runInfo.hyperparameters.augmentations}
                    <div class="card variant-ringed-primary p-4">
                      {#each runInfo.hyperparameters.augmentations ?? [] as augmentation, index (index)}
                        <div class="pb-2">
                          <div>
                            <label class="label pb-1" for="augmentationName-{index}"
                              >({index + 1}) Augmentation name:</label
                            >
                            <input
                              id="augmentationName-{index}"
                              name="augmentationName-{index}"
                              type="text"
                              class="input my-0 box-border flex w-fit px-2 py-1"
                              value={augmentation.name ?? ''}
                              onchange={(e) => {
                                if (prepareHyperParameters(runInfo, 'augmentations')) {
                                  runInfo.hyperparameters.augmentations[index].name =
                                    e.currentTarget.value
                                }
                              }}
                            />
                          </div>
                          <div class="pb-2">
                            <label
                              class="label flex cursor-pointer items-center space-x-2"
                              for="augmentationKwargsEnabled-{index}"
                            >
                              <input
                                type="checkbox"
                                id="augmentationKwargsEnabled-{index}"
                                name="augmentationKwargsEnabled-{index}"
                                class="checkbox"
                                checked={runInfo.hyperparameters.augmentations?.[index]?.kwargs !==
                                  undefined}
                                onchange={(e) => {
                                  if (prepareHyperParameters(runInfo, 'augmentations')) {
                                    if (e.currentTarget.checked) {
                                      runInfo.hyperparameters.augmentations[index].kwargs = {}
                                    } else {
                                      runInfo.hyperparameters.augmentations[index].kwargs =
                                        undefined
                                    }
                                  }
                                }}
                              />
                              <span>Set augmentation kwargs</span></label
                            >
                          </div>
                          {#if runInfo.hyperparameters.augmentations?.[index]?.kwargs !== undefined}
                            <KwargsInput
                              name="Augmentations kwargs"
                              bind:this={augmentationKwargsInput}
                              bind:kwargValues={runInfo.hyperparameters.augmentations[index].kwargs}
                              kwargTypes={[KwargType.INTEGER, KwargType.FLOAT]}
                            />
                          {/if}
                          <button
                            type="button"
                            class="variant-filled-error btn mt-2"
                            onclick={() => {
                              if (prepareHyperParameters(runInfo, 'augmentations')) {
                                runInfo.hyperparameters.augmentations.splice(index, 1)
                              }
                            }}>Remove augmentation</button
                          >
                        </div>
                      {/each}
                      <button
                        type="button"
                        class="variant-filled-primary btn mt-2"
                        disabled={runInfo.hyperparameters.augmentations?.some(
                          (augmentation) => !augmentation.name.trim(),
                        )}
                        onclick={() => {
                          if (prepareHyperParameters(runInfo, 'augmentations')) {
                            runInfo.hyperparameters.augmentations.push(new AugmentationParams())
                          }
                        }}>Add augmentation</button
                      >
                    </div>
                  {/if}
                </div>
                <div class="grid gap-2">
                  <h5 class="h5">Optimizer</h5>
                  <div class="pb-2">
                    <label class="label pb-1" for="optimizerName">Optimizer name</label>
                    <input
                      id="optimizerName"
                      name="optimizerName"
                      type="text"
                      class="input my-0 box-border flex w-fit px-2 py-1"
                      value={runInfo.hyperparameters.trainer?.optimizer?.name ?? ''}
                      onchange={(e) => {
                        if (prepareTrainerHyperParameters(runInfo, 'optimizer')) {
                          runInfo.hyperparameters.trainer.optimizer.name = e.currentTarget.value
                        }
                      }}
                    />
                  </div>
                  <div>
                    <label
                      class="label flex cursor-pointer items-center space-x-2"
                      for="optimizerKwargsEnabled"
                    >
                      <input
                        type="checkbox"
                        id="optimizerKwargsEnabled"
                        name="optimizerKwargsEnabled"
                        class="checkbox"
                        checked={runInfo.hyperparameters.trainer?.optimizer?.kwargs !== undefined}
                        onchange={(e) => {
                          if (prepareTrainerHyperParameters(runInfo, 'optimizer')) {
                            if (e.currentTarget.checked) {
                              runInfo.hyperparameters.trainer.optimizer.kwargs = {}
                            } else {
                              runInfo.hyperparameters.trainer.optimizer.kwargs = undefined
                            }
                          }
                        }}
                      />
                      <span>Set optimizer kwargs</span>
                    </label>
                  </div>
                  {#if runInfo.hyperparameters.trainer?.optimizer?.kwargs !== undefined}
                    <div class="card variant-ringed-primary p-4">
                      <KwargsInput
                        name="Optimizer kwargs"
                        bind:this={optimizerKwargsInput}
                        bind:kwargValues={runInfo.hyperparameters.trainer.optimizer.kwargs}
                        kwargTypes={[KwargType.FLOAT, KwargType.BOOLEAN]}
                      />
                    </div>
                  {/if}
                </div>
                <div class="grid gap-2">
                  <h5 class="h5">Learning rate finder</h5>
                  <div>
                    <label
                      class="label flex cursor-pointer items-center space-x-2"
                      for="learningRateFinderEnabled"
                    >
                      <input
                        type="checkbox"
                        id="learningRateFinderEnabled"
                        name="learningRateFinderEnabled"
                        class="checkbox"
                        checked={runInfo.hyperparameters.trainer?.lrFinder?.active ?? false}
                        onchange={(e) => {
                          if (prepareTrainerHyperParameters(runInfo, 'lrFinder')) {
                            runInfo.hyperparameters.trainer.lrFinder.active =
                              e.currentTarget.checked
                          }
                        }}
                      />
                      <span>Enable learning rate finder</span></label
                    >
                  </div>
                  {#if runInfo.hyperparameters.trainer?.lrFinder?.active}
                    <div class="card variant-ringed-primary grid grid-cols-3 gap-2 p-4">
                      <div>
                        <label class="label pb-1 text-sm" for="learningRateFinderMinLr"
                          >Minimum learning rate</label
                        >
                        <input
                          id="learningRateFinderMinLr"
                          name="learningRateFinderMinLr"
                          type="number"
                          class="input my-0 box-border flex w-fit px-2 py-1"
                          step={DEFAULT_LEARNING_RATE_FINDER_MIN_LR_STEP}
                          value={runInfo.hyperparameters.trainer?.lrFinder?.minLr ??
                            LrFinderParams.MIN_LR}
                          onchange={(e) => {
                            if (prepareTrainerHyperParameters(runInfo, 'lrFinder')) {
                              runInfo.hyperparameters.trainer.lrFinder.minLr = parseFloat(
                                e.currentTarget.value,
                              )
                            }
                          }}
                        />
                      </div>
                      <div>
                        <label class="label pb-1 text-sm" for="learningRateFinderMaxLr"
                          >Maximum learning rate</label
                        >
                        <input
                          id="learningRateFinderMaxLr"
                          name="learningRateFinderMaxLr"
                          type="number"
                          class="input my-0 box-border flex w-fit px-2 py-1"
                          step={DEFAULT_LEARNING_RATE_FINDER_MAX_LR_STEP}
                          value={runInfo.hyperparameters.trainer?.lrFinder?.maxLr ??
                            LrFinderParams.MAX_LR}
                          onchange={(e) => {
                            if (prepareTrainerHyperParameters(runInfo, 'lrFinder')) {
                              runInfo.hyperparameters.trainer.lrFinder.maxLr = parseFloat(
                                e.currentTarget.value,
                              )
                            }
                          }}
                        />
                      </div>
                      <div>
                        <label class="label pb-1 text-sm" for="learningRateFinderNumberOfSteps"
                          >Number of training steps</label
                        >
                        <input
                          id="learningRateFinderNumberOfSteps"
                          name="learningRateFinderNumberOfSteps"
                          type="number"
                          class="input my-0 box-border flex w-fit px-2 py-1"
                          step={1}
                          value={runInfo.hyperparameters.trainer?.lrFinder?.numberOfTrainingSteps ??
                            DEFAULT_LEARNING_RATE_FINDER_NUMBER_OF_TRAINING_STEPS}
                          onchange={(e) => {
                            if (prepareTrainerHyperParameters(runInfo, 'lrFinder')) {
                              runInfo.hyperparameters.trainer.lrFinder.numberOfTrainingSteps =
                                parseInt(e.currentTarget.value, 10)
                            }
                          }}
                        />
                      </div>
                    </div>
                  {/if}
                </div>
                <div class="grid gap-2">
                  <h5 class="h5">Learning rate scheduler</h5>
                  <div>
                    <label class="label pb-1" for="learningRateSchedulerName"
                      >Learning rate scheduler name</label
                    >
                    <input
                      id="learningRateSchedulerName"
                      name="learningRateSchedulerName"
                      type="text"
                      class="input my-0 box-border flex w-fit px-2 py-1"
                      value={runInfo.hyperparameters.trainer?.lrScheduler?.name ?? ''}
                      onchange={(e) => {
                        if (prepareTrainerHyperParameters(runInfo, 'lrScheduler')) {
                          runInfo.hyperparameters.trainer.lrScheduler.name = e.currentTarget.value
                        }
                      }}
                    />
                  </div>
                  <div class="pb-2">
                    <label
                      class="label flex cursor-pointer items-center space-x-2"
                      for="learningRateSchedulerKwargsEnabled"
                    >
                      <input
                        type="checkbox"
                        id="learningRateSchedulerKwargsEnabled"
                        name="learningRateSchedulerKwargsEnabled"
                        class="checkbox"
                        checked={runInfo.hyperparameters.trainer?.lrScheduler?.kwargs !== undefined}
                        onchange={(e) => {
                          if (prepareTrainerHyperParameters(runInfo, 'lrScheduler')) {
                            if (e.currentTarget.checked) {
                              runInfo.hyperparameters.trainer.lrScheduler.kwargs = {}
                            } else {
                              runInfo.hyperparameters.trainer.lrScheduler.kwargs = undefined
                            }
                          }
                        }}
                      />
                      <span> Set learning rate scheduler kwargs</span></label
                    >
                  </div>
                  {#if runInfo.hyperparameters.trainer?.lrScheduler?.kwargs !== undefined}
                    <div class="card variant-ringed-primary p-4">
                      <KwargsInput
                        name="Learning rate scheduler kwargs"
                        bind:this={lrSchedulerKwargsInput}
                        bind:kwargValues={runInfo.hyperparameters.trainer.lrScheduler.kwargs}
                        kwargTypes={[KwargType.FLOAT, KwargType.LIST_OF_FLOAT]}
                      />
                    </div>
                  {/if}
                </div>
                <div class="grid gap-2">
                  <h5 class="h5">Loss function</h5>
                  <div>
                    <label class="label pb-1" for="lossFunctionName">Loss function name</label>
                    <input
                      id="lossFunctionName"
                      name="lossFunctionName"
                      type="text"
                      class="input my-0 box-border flex w-fit px-2 py-1"
                      value={runInfo.hyperparameters.trainer?.lossFunction?.name ?? ''}
                      onchange={(e) => {
                        if (prepareTrainerHyperParameters(runInfo, 'lossFunction')) {
                          runInfo.hyperparameters.trainer.lossFunction.name = e.currentTarget.value
                        }
                      }}
                    />
                  </div>
                  <div class="pb-2">
                    <label
                      class="label flex cursor-pointer items-center space-x-2"
                      for="lossFunctionKwargsEnabled"
                    >
                      <input
                        type="checkbox"
                        id="lossFunctionKwargsEnabled"
                        name="lossFunctionKwargsEnabled"
                        class="checkbox"
                        checked={runInfo.hyperparameters.trainer?.lossFunction?.kwargs !==
                          undefined}
                        onchange={(e) => {
                          if (prepareTrainerHyperParameters(runInfo, 'lossFunction')) {
                            if (e.currentTarget.checked) {
                              runInfo.hyperparameters.trainer.lossFunction.kwargs = {}
                            } else {
                              runInfo.hyperparameters.trainer.lossFunction.kwargs = undefined
                            }
                          }
                        }}
                      />
                      <span>Set loss function kwargs</span>
                    </label>
                  </div>
                  {#if runInfo.hyperparameters.trainer?.lossFunction?.kwargs !== undefined}
                    <div class="card variant-ringed-primary p-4">
                      <KwargsInput
                        name="Loss function kwargs"
                        bind:this={lossFunctionKwargsInput}
                        bind:kwargValues={runInfo.hyperparameters.trainer.lossFunction.kwargs}
                        kwargTypes={[KwargType.FLOAT, KwargType.BOOLEAN, KwargType.REDUCTION_TYPES]}
                      />
                    </div>
                  {/if}
                  <div>
                    <label class="label pb-1" for="lossFunctionWeightingKind"
                      >Loss function class weighting</label
                    >
                    <Select
                      id="lossFunctionWeightingKind"
                      name="lossFunctionWeightingKind"
                      value={runInfo.hyperparameters.trainer?.lossFunction?.classWeighting ??
                        'uniform'}
                      onchange={(e) => {
                        if (prepareTrainerHyperParameters(runInfo, 'lossFunction')) {
                          runInfo.hyperparameters.trainer.lossFunction.classWeighting = e
                            .currentTarget.value as ClassWeighting
                        }
                      }}
                    >
                      {#each classWeightingValues as classWeighting (classWeighting)}
                        <option value={classWeighting}
                          >{displayClassWeighting[classWeighting]}</option
                        >
                      {/each}
                    </Select>
                  </div>
                </div>
                <h5 class="h5">Trainer configuration</h5>
                <div class="grid grid-cols-3 gap-2">
                  <div>
                    <label class="label pb-1" for="trainerMaxEpochs">Max epochs</label>
                    <input
                      id="trainerMaxEpochs"
                      name="trainerMaxEpochs"
                      type="number"
                      class="input my-0 box-border flex w-fit px-2 py-1"
                      step={1}
                      value={runInfo.hyperparameters.trainer?.maxEpochs ??
                        DEFAULT_TRAINER_MAX_EPOCHS}
                      onchange={(e) => {
                        if (prepareHyperParameters(runInfo, 'trainer')) {
                          runInfo.hyperparameters.trainer.maxEpochs = parseInt(
                            e.currentTarget.value,
                            10,
                          )
                        }
                      }}
                    />
                  </div>
                  <div>
                    <label class="label pb-1" for="trainerPatience">Patience</label>
                    <input
                      id="trainerPatience"
                      name="trainerPatience"
                      type="number"
                      class="input my-0 box-border flex w-fit px-2 py-1"
                      step={1}
                      value={runInfo.hyperparameters.trainer?.patience ?? DEFAULT_TRAINER_PATIENCE}
                      onchange={(e) => {
                        if (prepareHyperParameters(runInfo, 'trainer')) {
                          runInfo.hyperparameters.trainer.patience = parseInt(
                            e.currentTarget.value,
                            10,
                          )
                        }
                      }}
                    />
                  </div>
                  <div class="pb-2">
                    <label class="label pb-1" for="trainerBatchSize">Batch size</label>
                    <input
                      id="trainerBatchSize"
                      name="trainerBatchSize"
                      type="number"
                      class="input my-0 box-border flex w-fit px-2 py-1"
                      step={1}
                      value={runInfo.hyperparameters.trainer?.batchSize ??
                        DEFAULT_TRAINER_BATCH_SIZE}
                      onchange={(e) => {
                        if (prepareHyperParameters(runInfo, 'trainer')) {
                          runInfo.hyperparameters.trainer.batchSize = parseInt(
                            e.currentTarget.value,
                            10,
                          )
                        }
                      }}
                    />
                  </div>
                  <div>
                    <label
                      class="label flex cursor-pointer items-center space-x-2"
                      for="trainerAutoFindBatchSize"
                    >
                      <input
                        type="checkbox"
                        id="trainerAutoFindBatchSize"
                        name="trainerAutoFindBatchSize"
                        class="checkbox"
                        checked={runInfo.hyperparameters.trainer?.autoFindBatchSize ?? true}
                        onchange={(e) => {
                          if (prepareHyperParameters(runInfo, 'trainer')) {
                            runInfo.hyperparameters.trainer.autoFindBatchSize =
                              e.currentTarget.checked
                          }
                        }}
                      />
                      <span>Auto find batch size</span></label
                    >
                  </div>
                </div>
                <div class="grid gap-2">
                  <h5 class="h5">Image size</h5>
                  <div class="columns-2">
                    <div>
                      <label class="label pb-1" for="imgWidth">Image width</label>
                      <input
                        id="imgWidth"
                        name="imgWidth"
                        type="number"
                        class="input my-0 box-border flex w-fit px-2 py-1"
                        step={1}
                        value={runInfo.hyperparameters.imageSize?.[0] ?? DEFAULT_IMAGE_SIZE}
                        onchange={(e) => {
                          if (prepareHyperParameters(runInfo, 'imageSize')) {
                            runInfo.hyperparameters.imageSize[0] = parseInt(
                              e.currentTarget.value,
                              10,
                            )
                          }
                        }}
                      />
                    </div>
                    <div>
                      <label class="label pb-1" for="imgHeight">Image height</label>
                      <input
                        id="imgHeight"
                        name="imgHeight"
                        type="number"
                        class="input my-0 box-border flex w-fit px-2 py-1"
                        step={1}
                        value={runInfo.hyperparameters.imageSize?.[1] ?? DEFAULT_IMAGE_SIZE}
                        onchange={(e) => {
                          if (prepareHyperParameters(runInfo, 'imageSize')) {
                            runInfo.hyperparameters.imageSize[1] = parseInt(
                              e.currentTarget.value,
                              10,
                            )
                          }
                        }}
                      />
                    </div>
                  </div>
                  <aside class="alert variant-filled-surface m-2">
                    <div class="alert-message">
                      <p>
                        It is recommended to keep the image size at 224 for width and 224 for
                        height.
                      </p>
                      <p>
                        If instances of a given class are known to be small relative to the whole
                        image, you might want to consider increasing the image size.
                      </p>
                      <p>
                        Mostly relevant for classification problems or when the crop ratio is not
                        1.0
                      </p>
                    </div>
                  </aside>
                </div>
              </form>
            {/if}
          {/snippet}
        </AccordionItem>
      </Accordion>
    </article>
    <footer
      class="modal-footer sticky bottom-0 bg-surface-50 pb-2 pt-2 dark:bg-surface-800 {parent.regionFooter}"
    >
      <div class="variant-filled-primary btn-group w-min">
        <button class={parent.buttonNeutral} onclick={onFormCancel}
          >{parent.buttonTextCancel}</button
        >
        <button class={parent.buttonPositive} onclick={onFormSubmit}
          >{parent.buttonTextConfirm}</button
        >
      </div>
    </footer>
  </div>
{/if}
